import { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { useMaterialUIController } from "context";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAlert from "components/MDAlert";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { request } from "app/network";

function GifticonSettingsScreen(): JSX.Element {
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // Configuration States
    const [bizId, setBizId] = useState("");
    const [apiKey, setApiKey] = useState("");
    const [tokenKey, setTokenKey] = useState(""); // New: Token Key
    const [bannerId, setBannerId] = useState(""); // New: Banner ID
    const [cardId, setCardId] = useState("");     // New: Card ID
    const [callbackNo, setCallbackNo] = useState("");

    // To handle masked key
    const [originalApiKey, setOriginalApiKey] = useState("");
    const [originalTokenKey, setOriginalTokenKey] = useState("");

    // Config Keys
    const KEYS = {
        BIZ_ID: "GIFTISHOW_BIZ_ID",
        API_KEY: "GIFTISHOW_API_KEY",
        TOKEN_KEY: "GIFTISHOW_TOKEN_KEY", // New
        BANNER_ID: "GIFTISHOW_BANNER_ID", // New
        CARD_ID: "GIFTISHOW_CARD_ID",     // New
        CALLBACK_NO: "GIFTISHOW_CALLBACK_NO"
    };

    const maskApiKey = (key: string) => {
        if (!key) return "";
        if (key.length <= 8) return key;

        // Mask ~50% (Show 25% + 25%)
        const visibleLen = Math.floor(key.length / 4);
        const start = Math.max(4, visibleLen);
        const end = Math.max(4, visibleLen);

        if (start + end >= key.length) return key;

        const hiddenCount = key.length - (start + end);
        return `${key.substring(0, start)}${'*'.repeat(hiddenCount)}${key.substring(key.length - end)}`;
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const [bizIdRes, apiKeyRes, tokenKeyRes, bannerIdRes, cardIdRes, callbackNoRes] = await Promise.all([
                request("SystemConfig.GetConfig", { paramz: { Key: KEYS.BIZ_ID } }),
                request("SystemConfig.GetConfig", { paramz: { Key: KEYS.API_KEY } }),
                request("SystemConfig.GetConfig", { paramz: { Key: KEYS.TOKEN_KEY } }),
                request("SystemConfig.GetConfig", { paramz: { Key: KEYS.BANNER_ID } }),
                request("SystemConfig.GetConfig", { paramz: { Key: KEYS.CARD_ID } }),
                request("SystemConfig.GetConfig", { paramz: { Key: KEYS.CALLBACK_NO } }),
            ]);

            if (bizIdRes && bizIdRes.data) setBizId(bizIdRes.data.value || "");

            if (apiKeyRes && apiKeyRes.data) {
                const realKey = apiKeyRes.data.value || "";
                setOriginalApiKey(realKey);
                setApiKey(maskApiKey(realKey));
            }

            if (tokenKeyRes && tokenKeyRes.data) {
                const realKey = tokenKeyRes.data.value || "";
                setOriginalTokenKey(realKey);
                setTokenKey(maskApiKey(realKey));
            }

            if (bannerIdRes && bannerIdRes.data) setBannerId(bannerIdRes.data.value || "");
            if (cardIdRes && cardIdRes.data) setCardId(cardIdRes.data.value || "");
            if (callbackNoRes && callbackNoRes.data) setCallbackNo(callbackNoRes.data.value || "");

        } catch (error) {
            console.error("Failed to fetch Gifticon settings", error);
            setErrorMsg("설정을 불러오는데 실패했습니다.");
        } finally {
            setLoading(false);
        }
    };

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const theme = useTheme();

    const handleSave = async () => {
        if (!window.confirm("설정을 저장하시겠습니까?")) {
            return;
        }

        setLoading(true);
        setSuccessMsg(null);
        setErrorMsg(null);

        try {
            // Check if Keys are modified (not equal to masked original)
            const apiKeyToSend = (apiKey === maskApiKey(originalApiKey)) ? originalApiKey : apiKey;
            const tokenKeyToSend = (tokenKey === maskApiKey(originalTokenKey)) ? originalTokenKey : tokenKey;

            await Promise.all([
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.BIZ_ID, Value: bizId, Description: "기프티쇼 비즈 API 연동용 사용자 ID" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.API_KEY, Value: apiKeyToSend, Description: "기프티쇼 비즈 API 인증 코드 (보안)" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.TOKEN_KEY, Value: tokenKeyToSend, Description: "기프티쇼 비즈 API 토큰 키 (보안)" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.BANNER_ID, Value: bannerId, Description: "기프티쇼 비즈 배너 아이디" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.CARD_ID, Value: cardId, Description: "기프티쇼 비즈 카드 아이디" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.CALLBACK_NO, Value: callbackNo, Description: "MMS 발송 시 표시될 발신번호" } }),
            ]);

            setSuccessMsg("설정이 성공적으로 저장되었습니다.");

            // Update original keys if changed
            if (apiKeyToSend !== originalApiKey) {
                setOriginalApiKey(apiKeyToSend);
                setApiKey(maskApiKey(apiKeyToSend));
            }
            if (tokenKeyToSend !== originalTokenKey) {
                setOriginalTokenKey(tokenKeyToSend);
                setTokenKey(maskApiKey(tokenKeyToSend));
            }

            setTimeout(() => setSuccessMsg(null), 3000);
        } catch (error) {
            console.error("Failed to save Gifticon settings", error);
            setErrorMsg("설정 저장 중 오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">
                                    기프티콘 연동 설정 (기프티쇼 비즈)
                                </MDTypography>
                            </MDBox>
                            <MDBox p={3}>
                                {successMsg && (
                                    <MDBox mb={2}>
                                        <MDAlert color="success">{successMsg}</MDAlert>
                                    </MDBox>
                                )}
                                {errorMsg && (
                                    <MDBox mb={2}>
                                        <MDAlert color="error">{errorMsg}</MDAlert>
                                    </MDBox>
                                )}
                                <MDBox mb={2}>
                                    <MDTypography variant="body2" color="text">
                                        기프티쇼 비즈(Giftishow Biz) API 연동 정보를 설정합니다.
                                        <br />
                                        자세한 내용은{" "}
                                        <Link href="https://biz.giftishow.com" target="_blank" rel="noreferrer" sx={{ fontWeight: "bold" }}>
                                            https://biz.giftishow.com
                                        </Link>
                                        을 참조하세요.
                                    </MDTypography>
                                </MDBox>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="API User ID (biz_id)"
                                                fullWidth
                                                value={bizId}
                                                onChange={(e: any) => setBizId(e.target.value)}
                                                helperText="기프티쇼 비즈 API 연동용 사용자 ID"
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="API 발신번호 (callback_no)"
                                                fullWidth
                                                placeholder="예: 0212345678"
                                                value={callbackNo}
                                                onChange={(e: any) => setCallbackNo(e.target.value)}
                                                helperText="MMS 발송 시 표시될 발신번호 (사전 등록 필요)"
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="인증 Key (Authentication Key)"
                                                fullWidth
                                                multiline
                                                rows={2}
                                                value={apiKey}
                                                onChange={(e: any) => setApiKey(e.target.value)}
                                                helperText="기프티쇼 비즈 API 인증 코드 (보안 - 입력시 마스킹 처리됨)"
                                            />
                                        </MDBox>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="Token Key"
                                                fullWidth
                                                multiline
                                                rows={2}
                                                value={tokenKey}
                                                onChange={(e: any) => setTokenKey(e.target.value)}
                                                helperText="기프티쇼 비즈 API 토큰 키 (보안 - 입력시 마스킹 처리됨)"
                                            />
                                        </MDBox>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="배너 아이디 (Banner ID)"
                                                fullWidth
                                                value={bannerId}
                                                onChange={(e: any) => setBannerId(e.target.value)}
                                                helperText="프로모션 배너 식별 ID"
                                            />
                                        </MDBox>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="카드 아이디 (Card ID)"
                                                fullWidth
                                                value={cardId}
                                                onChange={(e: any) => setCardId(e.target.value)}
                                                helperText="기본 디자인 카드 식별 ID"
                                            />
                                        </MDBox>
                                    </Grid>
                                </Grid>

                                <Divider />

                                <MDBox display="flex" justifyContent="flex-end" mt={2} mb={1}>
                                    <MDButton
                                        variant="gradient"
                                        color="info"
                                        onClick={handleSave}
                                        disabled={loading}
                                    >
                                        {loading ? "저장 중..." : "설정 저장"}
                                    </MDButton>
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            <Footer />
        </DashboardLayout>
    );
}

export default GifticonSettingsScreen;
